import React, { ReactElement, useEffect, useState } from "react";
import { Translate } from "react-redux-i18n";
import { useHistory } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { MenuItem, Select, Typography } from "@mui/material";

import DetailsContent from "../DetailsContent";
import IntegralCombinations from "../IntegralCombinations";
import SystemCombinations from "../SystemCombinations";
import FontAwesomeIcon from "../../shared/FontAwesomeIcon";
import {
  clearCouponResponse,
  showPlayedCouponDetails,
  showPlayedCouponReceipt,
  getPlayedCouponRequested,
  saveCancelCouponId,
  saveCombinationFilter,
  getCashoutRequested,
} from "redux/coupon/couponSlice";
import { openDialog } from "redux/dialog/dialogSlice";
import { checkWalletPermissions } from "services/utils";
import { COMBINATIONS_FILTER_OPTIONS } from "../../coupon/constants";
import CustomBackdrop from "components/shared/CustomBackdrop";
import styles from './styles.module.scss'

export default function PlayedCouponDialog(): ReactElement {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const playedCoupon = useAppSelector(state => state.coupon.playedCoupon);
  const isPlayedCouponDetailVisible = useAppSelector(state => state.coupon.isPlayedCouponDetailVisible);
  const isPlayedCouponReceiptVisible = useAppSelector(state => state.coupon.isPlayedCouponReceiptVisible);
  const isFetchingPlayedCoupon = useAppSelector(state => state.coupon.isFetchingPlayedCoupon);
  const permissions = useAppSelector(state => state.auth.permissions);
  const betCancel = useAppSelector(state => state.config.skin?.setting.betcancel)
  const [isCombDialog, showCombDialog] = useState<boolean>(false)

  const handleCloseDetails = () => {
    dispatch(showPlayedCouponDetails(false))
    dispatch(clearCouponResponse())
  }
  const handleCloseCombinations = () => {
    showCombDialog(false)
  }
  const handleShowReceipt = () => {
    playedCoupon &&
      dispatch(getPlayedCouponRequested({ isRepeated: false, couponId: playedCoupon.id }))
    dispatch(showPlayedCouponReceipt(true))
  }
  const handleReplayCoupon = () => {
    playedCoupon &&
      dispatch(getPlayedCouponRequested({
        isRepeated: true,
        couponId: playedCoupon.id
      }))

    dispatch(showPlayedCouponDetails(false))
    history.push("/sport")
  }

  const handleCancelCoupon = (couponId?: string) => {
    dispatch(openDialog({ type: "cancel" }))
    couponId && dispatch(saveCancelCouponId(couponId))
  }

  const handleCashoutClick = (couponId: string) => {
    dispatch(getCashoutRequested(couponId))
    dispatch(openDialog({ type: "cashout", payload: couponId }))
  }

  const handleShowComb = () => {
    showCombDialog(true)
  }

  const handleCombinationsFilterChange = (state: string) => {
    dispatch(saveCombinationFilter(state))
  }

  useEffect(() => {
    isPlayedCouponDetailVisible && handleCloseDetails()
    isPlayedCouponReceiptVisible && dispatch(showPlayedCouponReceipt(false))
  }, []);


  return (
    <Dialog
      open={isPlayedCouponDetailVisible}
      disableEscapeKeyDown={true}
      fullWidth
      maxWidth={"xl"}
      className={styles.dialog}
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography className={styles.dialogTitleLabel}>
          <Translate value="couponDetail.couponDetailTitle" />&nbsp;{playedCoupon?.serial}
        </Typography>
        <IconButton onClick={handleCloseDetails} className={styles.closeIcon}>
          <FontAwesomeIcon type="fal" name="times" />
        </IconButton>
      </DialogTitle >
      < DialogContent className={styles.dialogContent}>
        {
          isFetchingPlayedCoupon ?
            <CustomBackdrop />
            :
            <Box>
              <DetailsContent />
              <Box className={styles.buttonGroupBetting}>
                <Box className={styles.cancelButtonContainer}>
                  {permissions && checkWalletPermissions(permissions, "game.betassist") && playedCoupon?.cancellable && betCancel == 1 &&
                    <>
                      <Button onClick={() => handleCancelCoupon(playedCoupon.id)} variant="contained" className={styles.cancelButton}>
                        <Typography>
                          <Translate value="coupon.cancelCoupon" />
                        </Typography>
                      </Button>
                      <IconButton onClick={() => handleCancelCoupon(playedCoupon.id)} className={styles.mobileIconButton}>
                        <FontAwesomeIcon name="trash" type="fas" />
                      </IconButton>
                    </>
                  }
                </Box>
                <Box className={`
                ${styles.buttonGroup}
                 ${(playedCoupon?.coupon_type.id === 3 || playedCoupon?.coupon_type.id === 4)
                  && (playedCoupon.replay || playedCoupon.cancellable)
                  && styles.flexWrapMobile}
                 `}>
                  {/* combinations */}
                  {playedCoupon?.cashout &&
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleCashoutClick(playedCoupon.id)}
                      className={styles.cashoutButton}
                    >
                      <Box className={styles.cashoutIcon}>
                        <FontAwesomeIcon name="euro-sign" type="fal" />
                      </Box>
                      <Typography className={styles.cashoutLabel}>Cashout</Typography>
                    </Button>
                  }
                  {(playedCoupon?.coupon_type.id === 3 || playedCoupon?.coupon_type.id === 4) &&
                    <Button
                      variant="contained"
                      color="info"
                      onClick={handleShowComb}
                      className={`${styles.button} ${playedCoupon.replay && styles.positionButtonLast}`}
                      startIcon={
                        <FontAwesomeIcon
                          type="fal"
                          name="chart-network"
                          width="15px"
                        />
                      }>
                      <Translate value="couponDetail.combinations" />
                    </Button>}
                  {/* replay */}
                  {
                    playedCoupon?.replay &&
                    permissions &&
                    (checkWalletPermissions(permissions, "game.bet") ||
                      checkWalletPermissions(permissions, "game.betassist")) &&
                    <Button
                      variant="contained"
                      color="success"
                      className={styles.button}
                      startIcon={
                        <FontAwesomeIcon
                          type="fal"
                          name="rotate-right"
                          width="15px"
                        />
                      }
                      onClick={handleReplayCoupon}
                    >
                      <Translate value="couponDetail.replay" />
                    </Button>
                  }
                  {/* view */}
                  {
                    playedCoupon?.played &&
                    permissions &&
                    (checkWalletPermissions(permissions, "game.bet") ||
                      checkWalletPermissions(permissions, "game.betassist")) &&
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handleShowReceipt}
                      className={styles.button}
                      startIcon={
                        <FontAwesomeIcon
                          type="fal"
                          name="eye"
                          width="15px"
                        />
                      }>
                      <Translate value="coupon.view" />
                    </Button>
                  }
                  {/* view */}
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleCloseDetails}
                    className={styles.button}
                    startIcon={
                      <FontAwesomeIcon
                        type="fal"
                        name="close"
                        width="15px"
                      />
                    }>
                    <Translate value="coupon.close" />
                  </Button>
                </Box>
              </Box>
            </Box>
        }
      </DialogContent >
      <Dialog
        open={isCombDialog}
        disableEscapeKeyDown={true}
        fullWidth
        maxWidth={"xl"}
        className={styles.dialog}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Typography className={styles.dialogTitleLabel}>
            <Translate value="couponDetail.combinationsTitle" />&nbsp;{playedCoupon?.serial}
          </Typography>
          <IconButton onClick={handleCloseCombinations} className={styles.closeIcon}>
            <FontAwesomeIcon type="fal" name="times" />
          </IconButton>
        </DialogTitle >
        <DialogContent
          className={`${styles.dialogContent} ${styles.dialogContentNoOverflow}`}
        >
          <Box className={styles.combinationsFilterContainer}>
            <Select
              variant="outlined"
              inputProps={{
                MenuProps: {
                  className: "form-label",
                },
              }}
              onChange={(e) => handleCombinationsFilterChange(e.target.value)}
              defaultValue={COMBINATIONS_FILTER_OPTIONS[0].value}
              className={styles.combinationsFilter}
            >
              {COMBINATIONS_FILTER_OPTIONS.map((option, index) =>
                <MenuItem
                  key={index}
                  value={option.value}
                  divider
                  className={styles.filterOption}
                >
                  <Translate value={`couponDetail.${option.value}`} />
                </MenuItem>
              )}
            </Select>
          </Box>
          {
            playedCoupon?.coupon_type.id === 3 ?
              <SystemCombinations />
              : playedCoupon?.coupon_type.id === 4 ?

                <IntegralCombinations />
                : null
          }
        </DialogContent>
      </Dialog >
    </Dialog >
  );
}
